import type { DefaultValues, Path, UseFormResetField, UseFormSetValue } from 'react-hook-form';

import type { SchemaCreditRequestForm } from '../../schemas/creditRequestForm.schema';
import type { LoanType } from '../../types';

export const isSameDay = (date1?: Date | null, date2?: Date | null) => {
    if (
        date1 instanceof Date &&
        !Number.isNaN(date1.getTime()) &&
        date2 instanceof Date &&
        !Number.isNaN(date2.getTime())
    ) {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }
    return false;
};

export const monthDiff = (d1: Date, d2: Date) => {
    const months = (d2.getFullYear() - d1.getFullYear()) * 12;
    const result = months + d2.getMonth() - d1.getMonth();
    return result <= 0 ? null : result;
};

export const calculateLastDate = (firstDate: Date, runtime: number | null): Date | null => {
    if (!runtime || Number(runtime) <= 0) {
        return null;
    }

    const lastDate = new Date(firstDate);
    lastDate.setMonth(lastDate.getMonth() + Number(runtime));

    return lastDate;
};

export const calculateFirstDate = (lastDate: Date, runtime: number): Date | null => {
    if (runtime <= 0) {
        return null;
    }

    const firstDate = new Date(lastDate);
    firstDate.setMonth(firstDate.getMonth() - runtime);

    return firstDate;
};

type Props = {
    type: LoanType;
    loanData:
        | SchemaCreditRequestForm['ratenkredite'][number]
        | SchemaCreditRequestForm['geschaeftskredite'][number]
        | SchemaCreditRequestForm['geschaeftskredite'][number];
    index: number;
    setValue: UseFormSetValue<SchemaCreditRequestForm>;
    resetField: UseFormResetField<SchemaCreditRequestForm>;
    triggerType?: 'kreditLaufzeit' | 'datumErsteZahlung' | 'datumLetzteRate';
    defaultValue?:
        | DefaultValues<SchemaCreditRequestForm['ratenkredite'][number]>
        | DefaultValues<SchemaCreditRequestForm['geschaeftskredite'][number]>
        | DefaultValues<SchemaCreditRequestForm['geschaeftskredite'][number]>;
};

export const calculateDateAndRuntime = ({
    defaultValue,
    index,
    loanData,
    resetField,
    setValue,
    triggerType,
    type,
}: Props) => {
    const { activeElement } = document;
    const activeElementName = activeElement?.getAttribute('name') || activeElement?.id;
    const runtimeName = `${type}.${index}.kreditLaufzeit` as Path<SchemaCreditRequestForm>;
    const lastDateName = `${type}.${index}.datumLetzteRate` as Path<SchemaCreditRequestForm>;
    const firstDateName = `${type}.${index}.datumErsteZahlung` as Path<SchemaCreditRequestForm>;

    const firstDate = loanData.datumErsteZahlung;
    const lastDate = loanData.datumLetzteRate;
    const runTime = loanData.kreditLaufzeit;

    const firstDateDirty = activeElementName === firstDateName;
    const lastDateDirty = activeElementName === lastDateName;
    const runtimeDirty = activeElementName === runtimeName;

    if (firstDate && lastDate && runTime && !runtimeDirty && !firstDateDirty && !lastDateDirty) {
        const calculatedRuntime = monthDiff(firstDate, lastDate);
        if (
            calculatedRuntime !== runTime &&
            (triggerType === 'datumErsteZahlung' || triggerType === 'datumLetzteRate')
        ) {
            if (defaultValue?.kreditLaufzeit === calculatedRuntime) {
                resetField(runtimeName);
            } else {
                setValue(runtimeName, calculatedRuntime);
            }
            return;
        }
        if (calculatedRuntime !== runTime && triggerType === 'kreditLaufzeit') {
            const newValue = calculateLastDate(firstDate, runTime);
            if (isSameDay(newValue, defaultValue?.datumLetzteRate)) {
                resetField(lastDateName);
            } else {
                setValue(lastDateName, newValue);
            }
            return;
        }
    }
    if (firstDate && lastDate && !runTime && !runtimeDirty) {
        const newValue = monthDiff(firstDate, lastDate);

        if (defaultValue?.kreditLaufzeit === newValue) {
            resetField(runtimeName);
        } else {
            setValue(runtimeName, newValue);
        }
    }
    if (firstDate && runTime && !lastDate && !lastDateDirty) {
        const newValue = calculateLastDate(firstDate, runTime);

        if (isSameDay(newValue, defaultValue?.datumLetzteRate)) {
            resetField(lastDateName);
        } else {
            setValue(lastDateName, newValue);
        }
    }
    if (lastDate && runTime && !firstDate && !firstDateDirty) {
        const newValue = calculateFirstDate(lastDate, runTime);
        if (isSameDay(newValue, defaultValue?.datumErsteZahlung)) {
            resetField(firstDateName);
        } else {
            setValue(firstDateName, newValue);
        }
    }
};
